import styled from 'styled-components'
import { BodyLarge, BodyXlMedium, RichTextLink, Title2 } from '../../../components/styles/Typography'
import { Wave } from '../../../components/wave/Wave'
import { theme } from '../../../theme/theme'
import { isMobile } from '../../../utils/devices'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { useGetUserHealthPlanQuery } from '../../../api/ester-b2b-api/private/users/userQueries'
import { CollectionCard } from '../../../components/CollectionCard'
import { getModuleTitle, Module, Modules } from '@estercare/ester-shared'
import { MarkdownToReact } from '../../../components/markdown/MardownToReact'
import { ErrorGuard } from '../../../components/ErrorGuard'
import { AnalysisLayout } from '../../../components/layouts/AnalysisLayout'
import { useTranslation } from 'react-i18next'
import { useSelectUserLanguage } from '../../../state/selectors'
import { Grid } from '../../../components/Grid'
import { BlockItem } from '../../../components/BlockItem'
import { Modal } from '../../../components/modals/Modal'
import { useModuleModal } from '../../../hooks/useModuleModal'
import { useScrollThreshold } from '../../../hooks/useScrollThreshold'
import { getModuleImageId, DEFAULT_MODULE_IMAGE } from '../../../utils/imageMapper'
import { useGetAssetById } from '../../../api/contentful/contentfulQueries'
import { parseContenfulImageUrl } from '../../../contentful/utils'

const stripMarkdown = (text: string): string => {
  return text
    .replace(/^\s*#{1,6}[^#\n]*\n?/gm, '') // Remove all heading levels (# to ######)
    .replace(/^\s*[-*]\s*\*?\*?\s*/gm, '') // Remove bullets like -, *, - **, etc
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markings **text**
    .replace(/\*(.*?)\*/g, '$1') // Remove italic markings *text*
    .replace(/\[(.*?)\](?:\([^)]*\))?/g, '$1') // Remove link brackets [text](url) or just [text]
    .trim()
}

const ModuleCard = ({
  module,
  onModuleClick,
}: {
  module: Module
  onModuleClick: (module: Module, title: string, imageUrl: string) => void
}) => {
  const language = useSelectUserLanguage()
  const { data: imageAsset } = useGetAssetById(getModuleImageId(module.id))

  const getFormattedTitle = (module: Module): string => {
    return language === null ? getModuleTitle(module.id) : getModuleTitle(module.id, language)
  }

  const title = getFormattedTitle(module)
  const imageUrl = imageAsset ? parseContenfulImageUrl(imageAsset, { width: 800, height: 600 }) : DEFAULT_MODULE_IMAGE

  return (
    <CollectionCard
      title={title}
      abstract={stripMarkdown(module.text)}
      squareImage={false}
      image={{
        url: imageUrl,
        description: 'Module image',
      }}
      onClick={() => onModuleClick(module, title, imageUrl)}
    />
  )
}

export const HealthPlan = () => {
  const { t } = useTranslation()
  const { data: healthPlan, isLoading: isHealthPlanLoading, isError, error } = useGetUserHealthPlanQuery()
  const width = useWindowWidth()
  const isMobileDevice = isMobile(width)

  const { selectedModule, openModal, closeModal, isOpen } = useModuleModal()
  const { scrollContainerRef, handleScroll, isScrolledPastThreshold, resetScrollPosition } = useScrollThreshold()

  const handleCloseModal = () => {
    closeModal()
    resetScrollPosition()
  }

  return (
    <>
      {!isMobileDevice && (
        <StyledWaveWrapper>
          <Wave color={theme.color.plum} />
        </StyledWaveWrapper>
      )}
      <AnalysisLayout backgroundColor={theme.color.plum} paddingTop={isMobileDevice ? theme.spacing.xxlarge : 0}>
        {isHealthPlanLoading ? (
          <LoadingSpinner color={theme.color.white} size={60} />
        ) : (
          <Container>
            <InnerContainer>
              <StyledBodyXlMedium>{t('employee.yourHealth.healthPlanSummary')}</StyledBodyXlMedium>
              <TextContainer>
                {isError || !healthPlan ? (
                  <ErrorGuard error={error} />
                ) : (
                  <MarkdownToReact markdown={healthPlan.summary} />
                )}
              </TextContainer>
              {healthPlan &&
                healthPlan.plan.map((plan, index) => (
                  <PlanContainer key={index}>
                    <StyledBodyXlMedium>{plan.title}</StyledBodyXlMedium>
                    <Grid $columns={2}>
                      {plan.modules
                        .sort((a, b) => Object.values(Modules).indexOf(a.id) - Object.values(Modules).indexOf(b.id))
                        .map((module, moduleIndex) => (
                          <BlockItem height={368} key={moduleIndex}>
                            <ModuleCard module={module} onModuleClick={openModal} />
                          </BlockItem>
                        ))}
                    </Grid>
                  </PlanContainer>
                ))}
              <StyledBodyXlMedium>{t('employee.yourHealth.careContactInfo')}</StyledBodyXlMedium>
              <TextContainer>
                <BodyLarge>
                  <StyledListItem>{t('employee.yourHealth.gynecologyInfo')}</StyledListItem>
                  <StyledListItem>
                    {t('employee.yourHealth.physicalClinicInfo')}{' '}
                    <RichTextLink
                      href="https://estercare.com/gynmottagning-torsplan"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('employee.yourHealth.readMoreHere')}
                    </RichTextLink>
                  </StyledListItem>
                  <StyledListItem>
                    {t('employee.yourHealth.digitalClinicInfo')}{' '}
                    <RichTextLink
                      href="https://estercare.com/sa-funkar-varden"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('employee.yourHealth.readMoreHere')}
                    </RichTextLink>
                  </StyledListItem>
                </BodyLarge>
              </TextContainer>
            </InnerContainer>
          </Container>
        )}
      </AnalysisLayout>
      {isOpen && selectedModule && (
        <Modal onClose={handleCloseModal} noPadding closeButtonVariant={isScrolledPastThreshold ? 'default' : 'light'}>
          <ModalContent ref={scrollContainerRef} onScroll={handleScroll}>
            <ModalHeader>
              <ModalHeaderImage src={selectedModule.image} />
              <ModalHeaderOverlay>
                <StyledTitle2>{selectedModule.title}</StyledTitle2>
              </ModalHeaderOverlay>
            </ModalHeader>
            <ModalBody>
              <MarkdownToReact markdown={selectedModule.text} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

const Container = styled.div`
  min-height: 80vh;
  display: flex;
  justify-content: center;
`

const InnerContainer = styled.div`
  width: 748px;
  max-width: 748px;
`

const TextContainer = styled.div`
  border-radius: ${theme.spacing.medium}px;
  padding: ${theme.spacing.xlarge}px;
  background-color: ${theme.color.white};
  margin-bottom: ${theme.spacing.xxlarge}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.medium}px;
  }
`

const StyledListItem = styled.li`
  margin-bottom: ${theme.spacing.mediumLarge}px;
`

const StyledWaveWrapper = styled.div`
  z-index: 0;
  margin-top: -${theme.spacing.xxlarge}px;
`

const StyledBodyXlMedium = styled(BodyXlMedium)`
  color: ${theme.color.white};
  margin-bottom: ${theme.spacing.medium}px;
  margin-left: ${theme.spacing.tiny}px;
`

const PlanContainer = styled.div`
  margin-bottom: ${theme.spacing.xxlarge}px;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 900px;
  max-height: 85vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  position: relative;
  flex: 0 0 30vh;
  overflow: hidden;
`

const ModalHeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ModalHeaderOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: ${theme.spacing.large}px;
  display: flex;
  align-items: flex-end;
`

const StyledTitle2 = styled(Title2)`
  color: ${theme.color.white};
`

const ModalBody = styled.div`
  padding: ${theme.spacing.xlarge}px;
  padding-right: ${theme.spacing.xxlarge}px;
  background-color: ${theme.color.white};
  line-height: 1.6;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.large}px;
    padding-right: ${theme.spacing.xxlarge}px;
  }
`
